import React from "react";
import { Sentry, captureError } from "../lib/sentry";

/** Brutalist fallback shown when a React subtree crashes. */
const Fallback = ({ resetError }) => (
    <div data-testid="error-boundary-fallback" className="max-w-md mx-auto my-24 px-4">
        <div className="border-4 border-black bg-[#FF00FF] text-white shadow-brutal-lg p-6">
            <p className="font-mono text-[10px] uppercase tracking-widest mb-3 border-2 border-white inline-block px-2 py-1">
                / Error /
            </p>
            <h2 className="font-display text-3xl font-black uppercase tracking-tighter leading-none mb-3">
                SOMETHING<br/>WENT SIDEWAYS.
            </h2>
            <p className="font-mono text-xs leading-relaxed text-white/90">
                We logged this on our end. Try reloading — if it happens again, drop us a note at <strong>hi@fitsme.shop</strong>.
            </p>
            <div className="flex gap-2 mt-5">
                <button
                    onClick={resetError}
                    data-testid="error-boundary-reset"
                    className="border-2 border-white bg-white text-black px-3 py-2 font-display font-black text-xs uppercase tracking-widest shadow-brutal-sm btn-press"
                >
                    Try again
                </button>
                <a
                    href="/"
                    className="border-2 border-white text-white px-3 py-2 font-display font-black text-xs uppercase tracking-widest shadow-brutal-sm btn-press hover:bg-white hover:text-[#FF00FF] transition-colors"
                >
                    Home
                </a>
            </div>
        </div>
    </div>
);

/** Wraps the app in Sentry's error boundary when a DSN is configured,
 * otherwise uses a plain React boundary so the same fallback ships everywhere. */
export class PlainErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }
    static getDerivedStateFromError(error) { return { error }; }
    componentDidCatch(error, info) {
        captureError(error, { componentStack: info?.componentStack });
    }
    resetError = () => this.setState({ error: null });
    render() {
        if (this.state.error) return <Fallback resetError={this.resetError} />;
        return this.props.children;
    }
}

// Stable module-scope reference so React doesn't unmount on every parent render.
const _sentryFallback = ({ resetError }) => <Fallback resetError={resetError} />;

export const ErrorBoundary = ({ children }) => {
    if (process.env.REACT_APP_SENTRY_DSN) {
        return (
            <Sentry.ErrorBoundary fallback={_sentryFallback}>
                {children}
            </Sentry.ErrorBoundary>
        );
    }
    return <PlainErrorBoundary>{children}</PlainErrorBoundary>;
};
